import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import './AdminContacts.css';

const AdminContacts = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch contact messages from Supabase
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('contact_messages')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        // Format contact data
        const formattedContacts = (data || []).map((contact) => ({
          id: contact.id,
          fullName: contact.full_name || 'N/A',
          email: contact.email || 'N/A',
          phone: contact.phone || 'N/A',
          subject: contact.subject || 'N/A',
          message: contact.message || 'N/A',
          status: contact.status || 'new',
          priority: contact.priority || 'normal',
          createdAt: contact.created_at 
            ? new Date(contact.created_at).toLocaleString('en-NG', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A',
          updatedAt: contact.updated_at 
            ? new Date(contact.updated_at).toLocaleString('en-NG', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A',
          resolvedAt: contact.resolved_at 
            ? new Date(contact.resolved_at).toLocaleString('en-NG', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : null,
          adminNotes: contact.admin_notes || '',
          responseSent: contact.response_sent || false,
          rawContact: contact
        }));

        setAllContacts(formattedContacts);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setError(err.message || 'Failed to fetch contact messages');
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // Filter contacts based on search and filters
  const filteredContacts = allContacts.filter((contact) => {
    const matchesSearch = 
      contact.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || contact.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

  // Handle status update
  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error: updateError } = await supabase
        .from('contact_messages')
        .update(updateData)
        .eq('id', contactId);

      if (updateError) throw updateError;

      // Update local state
      setAllContacts(prev => prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, status: newStatus, resolvedAt: newStatus === 'resolved' ? updateData.resolved_at : contact.resolvedAt }
          : contact
      ));

      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status: ' + err.message);
    }
  };

  // Handle priority update
  const handlePriorityUpdate = async (contactId, newPriority) => {
    try {
      const { error: updateError } = await supabase
        .from('contact_messages')
        .update({ 
          priority: newPriority,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId);

      if (updateError) throw updateError;

      // Update local state
      setAllContacts(prev => prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, priority: newPriority }
          : contact
      ));

      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      alert('Failed to update priority: ' + err.message);
    }
  };

  // Handle view details
  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new': return 'status-badge-new';
      case 'in_progress': return 'status-badge-in-progress';
      case 'resolved': return 'status-badge-resolved';
      case 'closed': return 'status-badge-closed';
      default: return 'status-badge-default';
    }
  };

  // Get priority badge class
  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'priority-badge-urgent';
      case 'high': return 'priority-badge-high';
      case 'normal': return 'priority-badge-normal';
      case 'low': return 'priority-badge-low';
      default: return 'priority-badge-normal';
    }
  };

  return (
    <AdminLayout pageTitle="Contact Messages">
      <div className="admin-contacts-page">
        {/* Header */}
        <div className="admin-contacts-header">
          <div className="admin-contacts-header-left">
            <h1 className="admin-contacts-title">Contact Messages</h1>
            <p className="admin-contacts-subtitle">
              Manage and respond to customer inquiries
            </p>
          </div>
          <div className="admin-contacts-header-right">
            <div className="admin-contacts-stats">
              <div className="admin-contacts-stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-value">{allContacts.length}</span>
              </div>
              <div className="admin-contacts-stat-item">
                <span className="stat-label">New</span>
                <span className="stat-value new-count">
                  {allContacts.filter(c => c.status === 'new').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="admin-contacts-filters">
          <div className="admin-contacts-search">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by name, email, subject, or message..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="admin-contacts-search-input"
            />
          </div>
          <div className="admin-contacts-filter-group">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="admin-contacts-filter-select"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="admin-contacts-filter-select"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="admin-contacts-error">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{error}</span>
          </div>
        )}

        {/* Contacts Table */}
        {loading ? (
          <div className="admin-contacts-loading">
            <div className="loading-spinner"></div>
            <p>Loading contact messages...</p>
          </div>
        ) : paginatedContacts.length === 0 ? (
          <div className="admin-contacts-empty">
            <div className="empty-icon">💬</div>
            <h3>No contact messages found</h3>
            <p>
              {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No contact messages have been submitted yet'}
            </p>
          </div>
        ) : (
          <>
            <div className="admin-contacts-table-wrapper">
              <table className="admin-contacts-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedContacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="contact-name-cell">
                        <div className="contact-name">{contact.fullName}</div>
                      </td>
                      <td className="contact-email-cell">
                        <a href={`mailto:${contact.email}`} className="contact-email-link">
                          {contact.email}
                        </a>
                      </td>
                      <td className="contact-phone-cell">
                        <a href={`tel:${contact.phone}`} className="contact-phone-link">
                          {contact.phone}
                        </a>
                      </td>
                      <td className="contact-subject-cell">
                        <div className="contact-subject">{contact.subject}</div>
                      </td>
                      <td>
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusUpdate(contact.id, e.target.value)}
                          className={`status-select ${getStatusBadgeClass(contact.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={contact.priority}
                          onChange={(e) => handlePriorityUpdate(contact.id, e.target.value)}
                          className={`priority-select ${getPriorityBadgeClass(contact.priority)}`}
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </td>
                      <td className="contact-date-cell">
                        <div className="contact-date">{contact.createdAt}</div>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewDetails(contact)}
                          className="view-details-button"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-contacts-pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedContact && (
          <div className="admin-contacts-modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="admin-contacts-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Contact Message Details</h2>
                <button
                  className="modal-close-button"
                  onClick={() => setShowDetailsModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-content">
                <div className="modal-section">
                  <h3>Contact Information</h3>
                  <div className="modal-info-grid">
                    <div className="modal-info-item">
                      <span className="info-label">Full Name:</span>
                      <span className="info-value">{selectedContact.fullName}</span>
                    </div>
                    <div className="modal-info-item">
                      <span className="info-label">Email:</span>
                      <a href={`mailto:${selectedContact.email}`} className="info-value-link">
                        {selectedContact.email}
                      </a>
                    </div>
                    <div className="modal-info-item">
                      <span className="info-label">Phone:</span>
                      <a href={`tel:${selectedContact.phone}`} className="info-value-link">
                        {selectedContact.phone}
                      </a>
                    </div>
                    <div className="modal-info-item">
                      <span className="info-label">Subject:</span>
                      <span className="info-value">{selectedContact.subject}</span>
                    </div>
                  </div>
                </div>
                <div className="modal-section">
                  <h3>Message</h3>
                  <div className="modal-message">
                    {selectedContact.message}
                  </div>
                </div>
                <div className="modal-section">
                  <h3>Status & Priority</h3>
                  <div className="modal-info-grid">
                    <div className="modal-info-item">
                      <span className="info-label">Status:</span>
                      <select
                        value={selectedContact.status}
                        onChange={(e) => handleStatusUpdate(selectedContact.id, e.target.value)}
                        className={`status-select ${getStatusBadgeClass(selectedContact.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div className="modal-info-item">
                      <span className="info-label">Priority:</span>
                      <select
                        value={selectedContact.priority}
                        onChange={(e) => handlePriorityUpdate(selectedContact.id, e.target.value)}
                        className={`priority-select ${getPriorityBadgeClass(selectedContact.priority)}`}
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-section">
                  <h3>Timestamps</h3>
                  <div className="modal-info-grid">
                    <div className="modal-info-item">
                      <span className="info-label">Created:</span>
                      <span className="info-value">{selectedContact.createdAt}</span>
                    </div>
                    <div className="modal-info-item">
                      <span className="info-label">Last Updated:</span>
                      <span className="info-value">{selectedContact.updatedAt}</span>
                    </div>
                    {selectedContact.resolvedAt && (
                      <div className="modal-info-item">
                        <span className="info-label">Resolved:</span>
                        <span className="info-value">{selectedContact.resolvedAt}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="modal-close-footer-button"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
